/**
 * Stockage local offline-first (ADR-010, Q152) : interface LocalStore commune,
 * servie par IndexedDB (Dexie) sur web/PWA et par SQLite chiffré
 * (@capacitor-community/sqlite) sur mobile Capacitor — même schéma logique,
 * implémentation choisie au démarrage selon la plateforme.
 *
 * Une base par compte (WEB1-01) : `myequip-<userId>`. Changer de compte change
 * de base ; celle du compte précédent n'est plus lue par l'application. Sans
 * compte connecté (connexion, portail client), une base d'appareil sans donnée
 * métier ; une assistance MyEquip a sa base jetable, effacée à sa fin.
 */
import Dexie, { type EntityTable } from 'dexie';
import { Capacitor } from '@capacitor/core';

export interface CachedEntity {
  id: string;
  kind: string; // intervention | customer | site | equipment | report | schedule_event | article | stock_level
  data: Record<string, unknown>;
  version: number;
  updatedAt: string;
}

export interface OutboxOperation {
  opId: string; // uuid généré côté client — idempotence serveur (Q122)
  entityKind: string;
  entityId: string;
  opKind: 'create' | 'update' | 'delete' | 'action';
  baseVersion?: number;
  /**
   * Valeur de chaque champ envoyé à la dernière confirmation du serveur
   * (FLD-9) : le serveur n'écrit un champ que s'il l'a encore, sinon conflit.
   */
  base?: Record<string, unknown>;
  patch: Record<string, unknown>;
  createdAt: string;
  status: 'attente' | 'envoi' | 'succes' | 'erreur' | 'conflit' | 'refusee';
  lastError?: string;
  attempts: number;
  /**
   * Après un échec passager (serveur indisponible, erreur 5xx) : pas de nouvel
   * essai automatique avant cette heure (délai croissant, plafonné). « Envoyer
   * maintenant » et « Réessayer » l'effacent. `null` l'efface aussi.
   */
  nextAttemptAt?: string | null;
  /** Compte qui a fait le geste : le serveur refuse de l'appliquer au nom d'un autre. */
  ownerUserId?: string;
}

// Private durable tie-breaker: UUIDs and millisecond timestamps do not encode
// insertion order. Allocate this number in the same transaction as the operation.
type StoredOperation = OutboxOperation & { queueOrder?: number };
const OUTBOX_SEQUENCE_KEY = 'outboxSequence';
function compareOperations(a: StoredOperation, b: StoredOperation): number {
  return a.createdAt.localeCompare(b.createdAt)
    || (a.queueOrder ?? 0) - (b.queueOrder ?? 0)
    || a.opId.localeCompare(b.opId);
}

export interface PendingMedia {
  localId: string;
  kind: string;
  blob: Blob;
  filename: string;
  /** `refusee` : le serveur a refusé le fichier (type, taille, droit) ; il ne repart plus seul. */
  status: 'attente' | 'envoi' | 'succes' | 'erreur' | 'refusee';
  serverFileId?: string;
  /** Motif du dernier échec ou du refus, tel que le serveur l'a donné. */
  lastError?: string;
  createdAt: string;
}

export interface SyncMeta {
  key: string;
  value: string;
}

export class OfflineDb extends Dexie {
  entities!: EntityTable<CachedEntity, 'id'>;
  outbox!: EntityTable<StoredOperation, 'opId'>;
  media!: EntityTable<PendingMedia, 'localId'>;
  meta!: EntityTable<SyncMeta, 'key'>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      entities: 'id, kind, updatedAt',
      outbox: 'opId, status, createdAt',
      media: 'localId, status',
      meta: 'key',
    });
    this.version(2).stores({
      outbox: 'opId, status, createdAt, &queueOrder, [createdAt+queueOrder]',
    }).upgrade(async (tx) => {
      const outbox = tx.table<StoredOperation, string>('outbox');
      // Old IndexedDB records contain no insertion sequence. Preserve their
      // existing chronological/UUID tie order; it cannot be recovered afterwards.
      const operations = (await outbox.toArray()).sort(compareOperations);
      for (const [index, op] of operations.entries()) op.queueOrder = index + 1;
      await outbox.bulkPut(operations);
      await tx.table<SyncMeta, string>('meta').put({
        key: OUTBOX_SEQUENCE_KEY, value: String(operations.length),
      });
    });
  }
}

// --- Interface commune (ADR-010) -------------------------------------------------------
export type OpStatus = OutboxOperation['status'];
export type MediaStatus = PendingMedia['status'];

export interface LocalStore {
  getEntity(id: string): Promise<CachedEntity | undefined>;
  putEntity(entity: CachedEntity): Promise<void>;
  bulkPutEntities(entities: CachedEntity[]): Promise<void>;
  listEntitiesByKind(kind: string): Promise<CachedEntity[]>;
  /** Nombre de copies d'un type, lu sur l'index sans charger les objets. */
  countEntitiesByKind(kind: string): Promise<number>;
  /** Retire des copies sorties du périmètre (suppression, réaffectation, droit retiré). */
  deleteEntities(ids: string[]): Promise<void>;

  addOperation(op: OutboxOperation): Promise<void>;
  updateOperation(opId: string, patch: Partial<OutboxOperation>): Promise<void>;
  listOperations(statuses: OpStatus[], limit?: number): Promise<OutboxOperation[]>;
  listOperationsForEntity(entityId: string, statuses: OpStatus[]): Promise<OutboxOperation[]>;
  countOperations(statuses: OpStatus[]): Promise<number>;
  purgeSucceededBefore(cutoffIso: string): Promise<void>;

  addMedia(media: PendingMedia): Promise<void>;
  updateMedia(localId: string, patch: Partial<PendingMedia>): Promise<void>;
  deleteMedia(localId: string): Promise<void>;
  getMedia(localId: string): Promise<PendingMedia | undefined>;
  listMedia(statuses: MediaStatus[], limit?: number): Promise<PendingMedia[]>;
  countMedia(statuses: MediaStatus[]): Promise<number>;
  /** Nombre et taille des fichiers gardés dans ces états, sans les charger (budget de l'appareil). */
  mediaUsage(statuses: MediaStatus[]): Promise<{ count: number; bytes: number }>;

  getMeta(key: string): Promise<string | undefined>;
  putMeta(key: string, value: string): Promise<void>;
  /** Méta dont la clé commence par `prefix` (brouillons d'un compte, curseurs). */
  listMeta(prefix: string): Promise<SyncMeta[]>;
  deleteMeta(key: string): Promise<void>;

  /** Retire les copies des données du serveur ; la file et les médias restent. */
  clearEntities(): Promise<void>;
  /** Supprime la base de l'appareil, file et médias compris. */
  destroy(): Promise<void>;
}

/** Implémentation web/PWA : IndexedDB via Dexie. */
export class DexieStore implements LocalStore {
  constructor(private readonly base: OfflineDb) {}

  getEntity(id: string): Promise<CachedEntity | undefined> {
    return this.base.entities.get(id);
  }
  async putEntity(entity: CachedEntity): Promise<void> {
    await this.base.entities.put(entity);
  }
  async bulkPutEntities(entities: CachedEntity[]): Promise<void> {
    await this.base.entities.bulkPut(entities);
  }
  listEntitiesByKind(kind: string): Promise<CachedEntity[]> {
    return this.base.entities.where('kind').equals(kind).toArray();
  }
  countEntitiesByKind(kind: string): Promise<number> {
    return this.base.entities.where('kind').equals(kind).count();
  }
  async deleteEntities(ids: string[]): Promise<void> {
    await this.base.entities.bulkDelete(ids);
  }

  async addOperation(op: OutboxOperation): Promise<void> {
    await this.base.transaction('rw', this.base.outbox, this.base.meta, async () => {
      const previous = await this.base.meta.get(OUTBOX_SEQUENCE_KEY);
      const queueOrder = Number(previous?.value ?? 0) + 1;
      await this.base.outbox.add({ ...op, queueOrder });
      await this.base.meta.put({ key: OUTBOX_SEQUENCE_KEY, value: String(queueOrder) });
    });
  }
  async updateOperation(opId: string, patch: Partial<OutboxOperation>): Promise<void> {
    await this.base.outbox.update(opId, patch);
  }
  listOperations(statuses: OpStatus[], limit = 50): Promise<OutboxOperation[]> {
    // Walk the chronological index across ALL statuses before batching, without
    // loading the entire queue just to sort a limited batch in memory.
    return this.base.outbox.orderBy('[createdAt+queueOrder]')
      .filter((op) => statuses.includes(op.status)).limit(limit).toArray();
  }
  listOperationsForEntity(entityId: string, statuses: OpStatus[]): Promise<OutboxOperation[]> {
    return this.base.outbox.orderBy('[createdAt+queueOrder]')
      .filter((op) => op.entityId === entityId && statuses.includes(op.status)).toArray();
  }
  countOperations(statuses: OpStatus[]): Promise<number> {
    return this.base.outbox.where('status').anyOf(...statuses).count();
  }
  async purgeSucceededBefore(cutoffIso: string): Promise<void> {
    await this.base.outbox.where('status').equals('succes')
      .and((op) => op.createdAt < cutoffIso).delete();
  }

  async addMedia(media: PendingMedia): Promise<void> {
    await this.base.media.add(media);
  }
  async updateMedia(localId: string, patch: Partial<PendingMedia>): Promise<void> {
    await this.base.media.update(localId, patch);
  }
  async deleteMedia(localId: string): Promise<void> {
    await this.base.media.delete(localId);
  }
  getMedia(localId: string): Promise<PendingMedia | undefined> {
    return this.base.media.get(localId);
  }
  listMedia(statuses: MediaStatus[], limit = 5): Promise<PendingMedia[]> {
    return this.base.media.where('status').anyOf(...statuses).limit(limit).toArray();
  }
  countMedia(statuses: MediaStatus[]): Promise<number> {
    return this.base.media.where('status').anyOf(...statuses).count();
  }
  async mediaUsage(statuses: MediaStatus[]): Promise<{ count: number; bytes: number }> {
    let count = 0;
    let bytes = 0;
    await this.base.media.where('status').anyOf(...statuses).each((media) => {
      count += 1;
      bytes += media.blob.size;
    });
    return { count, bytes };
  }

  async getMeta(key: string): Promise<string | undefined> {
    return (await this.base.meta.get(key))?.value;
  }
  async putMeta(key: string, value: string): Promise<void> {
    await this.base.meta.put({ key, value });
  }
  listMeta(prefix: string): Promise<SyncMeta[]> {
    return this.base.meta.where('key').startsWith(prefix).toArray();
  }
  async deleteMeta(key: string): Promise<void> {
    await this.base.meta.delete(key);
  }

  async clearEntities(): Promise<void> {
    await this.base.entities.clear();
  }
  async destroy(): Promise<void> {
    await this.base.delete();
  }
}

// --- Base du compte actif ---------------------------------------------------------

/** Compte dont la base est ouverte : un membre, ou une assistance MyEquip. */
export type StoreScope =
  | { kind: 'account'; userId: string }
  | { kind: 'support'; userId: string; sessionId: string };

/** Base unique d'avant le cloisonnement : reprise une fois par le compte actif. */
export const LEGACY_STORE_NAME = 'myequip';
const DEVICE_STORE_NAME = 'myequip-appareil';

const safe = (id: string): string => id.replace(/[^0-9A-Za-z-]/g, '');

export function storeName(scope: StoreScope | undefined): string {
  if (!scope) return DEVICE_STORE_NAME;
  return scope.kind === 'account'
    ? `myequip-${safe(scope.userId)}`
    : `myequip-assistance-${safe(scope.sessionId)}`;
}

let activeScope: StoreScope | undefined;
const opened = new Map<string, Promise<LocalStore>>();
let testStore: Promise<LocalStore> | undefined;

/**
 * Désigne la base du compte connecté, avant tout rendu qui la lit. Les
 * lectures suivantes (`getLocalStore`) ne voient plus celle du compte précédent.
 */
export function selectStoreScope(scope: StoreScope | undefined): boolean {
  const changed = storeName(scope) !== storeName(activeScope);
  activeScope = scope;
  testStore = undefined;
  return changed;
}

/** Compte auquel appartiennent les gestes mis en file maintenant. */
export function activeStoreOwner(): string | undefined {
  return activeScope?.userId;
}

/** Pour les tests : injecter une base isolée. */
export function setOfflineDbForTests(instance: OfflineDb): void {
  testStore = Promise.resolve(new DexieStore(instance));
}

/** Ouverture selon la plateforme (Q152) : SQLite chiffré sur mobile natif, Dexie sinon.
 * L'implémentation SQLite est importée dynamiquement pour rester hors du bundle web. */
async function openBackend(name: string): Promise<LocalStore> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { createSqliteStore } = await import('./store-sqlite.js');
      return await createSqliteStore(name);
    } catch {
      // Plugin absent (build web empaqueté) : repli IndexedDB, jamais bloquant
    }
  }
  return new DexieStore(new OfflineDb(name));
}

/** L'ancienne base partagée, si elle existe encore sur cet appareil. */
async function openLegacy(): Promise<LocalStore | undefined> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { openExistingSqliteStore } = await import('./store-sqlite.js');
      return await openExistingSqliteStore(LEGACY_STORE_NAME);
    } catch {
      // Plugin absent : l'ancienne base éventuelle est en IndexedDB
    }
  }
  return (await Dexie.exists(LEGACY_STORE_NAME))
    ? new DexieStore(new OfflineDb(LEGACY_STORE_NAME))
    : undefined;
}

/** Ce qui n'est pas encore réglé avec le serveur : seul contenu repris de l'ancienne base. */
const UNSETTLED_OPERATIONS: OpStatus[] = ['attente', 'erreur', 'envoi', 'conflit', 'refusee'];
const LEGACY_LIMIT = 10_000;

/**
 * Reprise unique de l'ancienne base partagée par le compte actif (WEB1-01) :
 * ses envois non réglés, leurs photos et SES brouillons. Les copies des
 * données du serveur ne sont pas reprises (un pull complet suit) ; les
 * brouillons d'un autre compte non plus. Rejouable : ce qui est déjà repris
 * n'est pas dupliqué si la reprise a été interrompue.
 */
async function adoptLegacyStore(
  legacy: LocalStore, target: LocalStore, userId: string,
): Promise<void> {
  const known = new Set(
    (await target.listOperations(UNSETTLED_OPERATIONS, LEGACY_LIMIT)).map((op) => op.opId),
  );
  const operations = await legacy.listOperations(UNSETTLED_OPERATIONS, LEGACY_LIMIT);
  const referenced = new Set(operations.flatMap((op) => {
    const refs = op.patch.mediaLocalIds;
    return Array.isArray(refs) ? refs.filter((ref): ref is string => typeof ref === 'string') : [];
  }));
  for (const media of await legacy.listMedia(['attente', 'erreur', 'envoi', 'succes'], LEGACY_LIMIT)) {
    if (media.status === 'succes' && !referenced.has(media.localId)) continue;
    if (await target.getMedia(media.localId)) continue;
    await target.addMedia({ ...media, status: media.status === 'envoi' ? 'attente' : media.status });
  }
  for (const op of operations) {
    if (known.has(op.opId)) continue;
    await target.addOperation({
      ...op, status: op.status === 'envoi' ? 'attente' : op.status, ownerUserId: userId,
    });
  }
  for (const draft of await legacy.listMeta(`draft.v1.${userId}.`)) {
    // Un brouillon vide est un formulaire laissé intact : rien à reprendre.
    if (draft.value !== '') await target.putMeta(draft.key, draft.value);
  }
  await legacy.destroy();
}

async function open(name: string, scope: StoreScope | undefined): Promise<LocalStore> {
  const store = await openBackend(name);
  if (scope?.kind === 'account') {
    try {
      const legacy = await openLegacy();
      if (legacy) await adoptLegacyStore(legacy, store, scope.userId);
    } catch {
      // Reprise impossible maintenant : l'ancienne base reste intacte, nouvel essai au prochain démarrage.
    }
  }
  return store;
}

/** Base d'un compte donné, ouverte une seule fois par lancement de l'application. */
export function openLocalStore(scope: StoreScope | undefined): Promise<LocalStore> {
  const name = storeName(scope);
  let store = opened.get(name);
  if (!store) {
    store = open(name, scope);
    opened.set(name, store);
    // Un échec d'ouverture ne reste pas en mémoire : le prochain appel réessaie.
    store.catch(() => opened.delete(name));
  }
  return store;
}

/**
 * Envois (opérations et médias) qui attendent dans la base d'une adhésion, sans
 * la créer ni la reprendre si elle n'existe pas : pastille du sélecteur
 * d'entreprise. Une base déjà ouverte est lue telle quelle ; une autre est
 * ouverte le temps du comptage, puis refermée.
 */
export async function countWaitingIn(
  scope: StoreScope, operations: OpStatus[], media: MediaStatus[],
): Promise<number> {
  const name = storeName(scope);
  const count = async (store: LocalStore): Promise<number> =>
    (await store.countOperations(operations)) + (await store.countMedia(media));
  const known = opened.get(name);
  if (known) return count(await known);
  if (Capacitor.isNativePlatform()) {
    try {
      const { openExistingSqliteStore } = await import('./store-sqlite.js');
      const store = await openExistingSqliteStore(name);
      return store ? count(store) : 0;
    } catch {
      // Plugin absent : la base éventuelle est en IndexedDB
    }
  }
  if (!(await Dexie.exists(name))) return 0;
  const base = new OfflineDb(name);
  try {
    return await count(new DexieStore(base));
  } finally {
    base.close();
  }
}

/** Supprime de l'appareil la base d'un compte, file et médias compris. */
export async function destroyLocalStore(scope: StoreScope | undefined): Promise<void> {
  const name = storeName(scope);
  const store = await openLocalStore(scope);
  opened.delete(name);
  await store.destroy();
}

/** Base du compte actif. */
export function getLocalStore(): Promise<LocalStore> {
  return testStore ?? openLocalStore(activeScope);
}
